import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Input, Space, Tag, Typography, App, theme } from 'antd';
import { ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import UserBulkInviteModal from './UserBulkInviteModal';

const { Text } = Typography;

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'canceled';

export interface InvitationListItem {
  id: string;
  email: string;
  name: string | null;
  role: string;
  inviterName: string | null;
  inviterEmail: string | null;
  invitedBy: string | null;
  status: InvitationStatus;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  canceledAt: string | null;
  message: string | null;
}

export interface BulkUploadResult {
  processed: number;
  succeeded: number;
  failed: number;
  errors?: Array<{ row: number; message: string }>;
}

export interface InvitationManagementProps {
  invitations: InvitationListItem[];
  loading?: boolean;
  initialPageSize?: number;
  onResend?: (invitationId: string) => Promise<void>;
  onCancel?: (invitationId: string) => Promise<void>;
  onRefresh?: () => void;
  showBulkUpload?: boolean;
  companyId?: string;
}

type ActionState =
  | { type: 'resend'; id: string }
  | { type: 'cancel'; id: string }
  | null;

const STATUS_LABEL: Record<InvitationStatus, string> = {
  pending: '未対応',
  accepted: '受諾済み',
  expired: '期限切れ',
  canceled: 'キャンセル済み',
};

const STATUS_COLOR: Record<InvitationStatus, string> = {
  pending: 'blue',
  accepted: 'green',
  expired: 'orange',
  canceled: 'default',
};

const ROLE_LABEL: Record<string, string> = {
  admin: '管理者',
  manager: 'マネージャー',
  user: 'ユーザー',
  viewer: '閲覧者',
};

function getRoleText(role: string): string {
  return ROLE_LABEL[role] ?? role;
}

function formatDate(value: string | null) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export const InvitationManagement: React.FC<InvitationManagementProps> = ({
  invitations,
  loading = false,
  initialPageSize = 10,
  onResend,
  onCancel,
  onRefresh,
  showBulkUpload = true,
  companyId,
}) => {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [localInvitations, setLocalInvitations] = useState<InvitationListItem[]>(invitations);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | InvitationStatus>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [actionState, setActionState] = useState<ActionState>(null);
  const [bulkInviteModalVisible, setBulkInviteModalVisible] = useState(false);

  useEffect(() => {
    setLocalInvitations(invitations);
  }, [invitations]);

  const filteredInvitations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return localInvitations.filter((invitation) => {
      const matchesStatus =
        statusFilter === 'all' ? true : invitation.status === statusFilter;

      if (!matchesStatus) {
        return false;
      }

      if (!term) {
        return true;
      }

      const haystack = [
        invitation.email.toLowerCase(),
        invitation.name?.toLowerCase() ?? '',
        invitation.inviterName?.toLowerCase() ?? '',
        invitation.inviterEmail?.toLowerCase() ?? '',
      ];
      return haystack.some((value) => value.includes(term));
    });
  }, [localInvitations, searchTerm, statusFilter]);

  const paginatedInvitations = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredInvitations.slice(start, start + pageSize);
  }, [filteredInvitations, currentPage, pageSize]);

  const totalItems = filteredInvitations.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const handleResend = async (invitationId: string) => {
    if (!onResend) {
      return;
    }
    setActionState({ type: 'resend', id: invitationId });
    try {
      await onResend(invitationId);
      message.success('招待メールを再送信しました');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : '再送信に失敗しました';
      message.error(errorMessage);
    } finally {
      setActionState(null);
    }
  };

  const handleCancel = async (invitationId: string) => {
    if (!onCancel) {
      setConfirmingId(null);
      return;
    }
    setActionState({ type: 'cancel', id: invitationId });
    try {
      await onCancel(invitationId);
      setLocalInvitations((prev) =>
        prev.map((invitation) =>
          invitation.id === invitationId
            ? {
                ...invitation,
                status: 'canceled',
                canceledAt: new Date().toISOString(),
              }
            : invitation,
        ),
      );
      message.success('招待を取り消しました');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : '取り消しに失敗しました';
      message.error(errorMessage);
    } finally {
      setConfirmingId(null);
      setActionState(null);
    }
  };

  return (
    <Card
      variant="borderless"
      title="招待管理"
      extra={
        <Space>
          <Input.Search
            allowClear
            placeholder="メールまたは氏名で検索"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setCurrentPage(1);
            }}
            style={{ width: 220 }}
          />
          <select
            aria-label="ステータスフィルター"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as 'all' | InvitationStatus);
              setCurrentPage(1);
            }}
            style={{ minWidth: 160, padding: '6px 8px' }}
          >
            <option value="all">すべて</option>
            <option value="pending">{STATUS_LABEL.pending}</option>
            <option value="accepted">{STATUS_LABEL.accepted}</option>
            <option value="expired">{STATUS_LABEL.expired}</option>
            <option value="canceled">{STATUS_LABEL.canceled}</option>
          </select>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setCurrentPage(1);
              onRefresh?.();
            }}
          >
            更新
          </Button>
        </Space>
      }
    >
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{ width: '100%', borderCollapse: 'collapse' }}
          data-testid="invitation-table"
        >
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '12px 8px' }}>招待先</th>
              <th style={{ textAlign: 'left', padding: '12px 8px' }}>権限</th>
              <th style={{ textAlign: 'left', padding: '12px 8px' }}>ステータス</th>
              <th style={{ textAlign: 'left', padding: '12px 8px' }}>招待者</th>
              <th style={{ textAlign: 'left', padding: '12px 8px' }}>送信日時</th>
              <th style={{ textAlign: 'left', padding: '12px 8px' }}>期限</th>
              <th style={{ textAlign: 'left', padding: '12px 8px' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {paginatedInvitations.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 16, textAlign: 'center', color: token.colorTextSecondary }}>
                  {loading ? '読み込み中...' : '招待が見つかりません'}
                </td>
              </tr>
            ) : (
              paginatedInvitations.map((invitation) => {
                const isResending =
                  actionState?.type === 'resend' && actionState.id === invitation.id;
                const isCanceling =
                  actionState?.type === 'cancel' && actionState.id === invitation.id;
                const isFinalized =
                  invitation.status === 'accepted' || invitation.status === 'canceled';
                return (
                  <tr key={invitation.id} style={{ borderTop: `1px solid ${token.colorBorderSecondary}` }}>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ fontWeight: 500 }}>{invitation.name ?? invitation.email}</div>
                      {invitation.name ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {invitation.email}
                        </Text>
                      ) : null}
                    </td>
                    <td style={{ padding: '12px 8px' }}>{getRoleText(invitation.role)}</td>
                    <td style={{ padding: '12px 8px' }}>
                      <Tag color={STATUS_COLOR[invitation.status]}>
                        {STATUS_LABEL[invitation.status]}
                      </Tag>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {invitation.inviterName ?? invitation.inviterEmail ?? '-'}
                    </td>
                    <td style={{ padding: '12px 8px' }}>{formatDate(invitation.createdAt)}</td>
                    <td style={{ padding: '12px 8px' }}>{formatDate(invitation.expiresAt)}</td>
                    <td style={{ padding: '12px 8px' }}>
                      <Space size="small">
                        <Button
                          size="small"
                          disabled={isResending || invitation.status === 'canceled'}
                          loading={isResending}
                          onClick={() => handleResend(invitation.id)}
                        >
                          再送信
                        </Button>
                        {confirmingId === invitation.id ? (
                          <Space size="small">
                            <Button
                              size="small"
                              danger
                              loading={isCanceling}
                              onClick={() => handleCancel(invitation.id)}
                            >
                              確認
                            </Button>
                            <Button
                              size="small"
                              onClick={() => setConfirmingId(null)}
                            >
                              戻る
                            </Button>
                          </Space>
                        ) : (
                          <Button
                            size="small"
                            danger
                            disabled={isFinalized}
                            onClick={() => setConfirmingId(invitation.id)}
                          >
                            取消
                          </Button>
                        )}
                      </Space>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalItems > 0 && (
        <Space
          style={{ marginTop: 16 }}
          wrap
        >
          <Button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            前へ
          </Button>
          {Array.from({ length: totalPages }).map((_, index) => {
            const page = index + 1;
            return (
              <Button
                key={page}
                type={page === currentPage ? 'primary' : 'default'}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </Button>
            );
          })}
          <Button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            次へ
          </Button>
          <select
            aria-label="ページサイズ"
            value={pageSize}
            onChange={(event) => {
              const size = Number(event.target.value);
              setPageSize(size);
              setCurrentPage(1);
            }}
            style={{ minWidth: 140, padding: '6px 8px' }}
          >
            <option value={10}>10件表示</option>
            <option value={20}>20件表示</option>
            <option value={50}>50件表示</option>
          </select>
        </Space>
      )}

      {showBulkUpload && companyId && (
        <>
          <Card
            size="small"
            className="mt-6"
            title="CSV一括招待"
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Text type="secondary">
                CSVファイルから複数ユーザーを一括で招待できます。
                各ユーザーに招待メールが送信され、メール内のURLからパスワードを設定できます。
              </Text>
              <div>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setBulkInviteModalVisible(true)}
                >
                  CSV一括招待を開始
                </Button>
              </div>
            </Space>
          </Card>

          {/* CSV一括招待モーダル */}
          <UserBulkInviteModal
            visible={bulkInviteModalVisible}
            onCancel={() => setBulkInviteModalVisible(false)}
            onSuccess={() => {
              setBulkInviteModalVisible(false);
              onRefresh?.();
            }}
            companyId={companyId}
          />
        </>
      )}
    </Card>
  );
};

export default InvitationManagement;
